import { TitleCasePipe, NgIf, NgFor, AsyncPipe, NgClass } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostListener,
	OnDestroy,
	OnInit,
	TrackByFunction,
	ViewChild,
} from '@angular/core';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import {
	defaultSalesPrice,
	isProductAvailableString,
	productDetailsConstants,
	TealiumProductList,
	TealiumUtagService,
	productViewConstants,
} from '@woolworthsnz/analytics';
import { BannerTarget, DynamicContentService, DynamicContentTargets } from '@woolworthsnz/dynamic-content';
import { distinctUntilFulfilmentChanged, FulfilmentStoreService } from '@woolworthsnz/fulfilment';
import {
	AddToTrolleyComponent,
	SeoComponent,
	TagComponent,
	FuelTagComponent,
	ProductImageComponent,
	PriceMetaComponent,
	PriceComponent,
	EachKgSelectorComponent,
	PriceSingleUnitAverageComponent,
	ShopperNotesComponent,
	ProductDetails,
	ProductService,
	ProductState,
	PricingService,
} from '@woolworthsnz/product';
import { MODAL_ROUTES } from '@woolworthsnz/shop';
import {
	AccordionItemComponent,
	AppSettingsService,
	DatalayerService,
	FeatureService,
	kebabCase,
	MetaDataService,
	ModalRoutingService,
	NotificationType,
	TrackingEvent,
	ButtonComponent,
	TrackEventDirective,
	SvgIconComponent,
	AlertComponent,
	AccordionComponent,
	NotificationComponent,
	EmbeddedVisibilityDirective,
	ImageZoomDirective,
	CapitalizeSpecificWordsPipe,
	getProductType,
	CallToActionResponse,
	getDiscountDiffValue,
} from '@woolworthsnz/styleguide';
import {
	ContextResponse,
	DynamicContentRequest,
	FulfilmentStorePickupAddressResponse,
	FulfilmentStoreStockLevelResponse,
	ImageInfo,
	NutritionResponse,
	ProductDetailsResponse,
	ProductResponse,
	ProductStoresStockLevelResponse,
	ProductTag,
	QuantityResponse,
	RewardValue,
	ShoppingListItem,
} from '@woolworthsnz/trader-api';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map, mergeMap, skip, take, takeUntil } from 'rxjs/operators';
import { BreadCrumbService } from '../../../core/services/breadcrumb.service';
import { ProductStockLevelIndicatorComponent } from '../../components';
import { pageAnalytics } from '../../../core/constants';
import StockLevelEnum = FulfilmentStoreStockLevelResponse.StockLevelEnum;

import {
	EDRBoostDetailsCardComponent,
	EDRRoundelComponent,
	EDRRoundelText,
	mapRewardTypeToRoundelText,
} from '@woolworthsnz/everyday-rewards';
const tagTypeEnum = ProductTag.TagTypeEnum;

/* eslint-disable @typescript-eslint/naming-convention */
export enum ListErrorMessages {
	LoadFailed = 'There was a problem loading your saved lists',
	SaveFailed = 'There was a problem saving the product to your list',
	RemoveFailed = 'There was a problem removing the product from your list',
}

/* eslint-enable @typescript-eslint/naming-convention */

@Component({
	selector: 'wnz-product-detail',
	templateUrl: './detail.page.html',
	styleUrls: ['./detail.page.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [ProductService],
	standalone: true,
	imports: [
		NgIf,
		NgClass,
		SeoComponent,
		TagComponent,
		NgFor,
		FuelTagComponent,
		ProductImageComponent,
		ImageZoomDirective,
		PriceMetaComponent,
		PriceSingleUnitAverageComponent,
		PriceComponent,
		EachKgSelectorComponent,
		ProductStockLevelIndicatorComponent,
		NotificationComponent,
		AddToTrolleyComponent,
		ButtonComponent,
		TrackEventDirective,
		SvgIconComponent,
		AccordionComponent,
		AccordionItemComponent,
		EmbeddedVisibilityDirective,
		AlertComponent,
		ShopperNotesComponent,
		RouterOutlet,
		AsyncPipe,
		TitleCasePipe,
		CapitalizeSpecificWordsPipe,
		EDRBoostDetailsCardComponent,
		EDRRoundelComponent,
	],
})
export class ProductDetailPage implements OnInit, OnDestroy {
	@ViewChild('shelfBanner') shelfBanner: ElementRef;
	@ViewChild('headerBanner') headerBanner: ElementRef;
	@ViewChild('dynamicContentGlobal') dynamicContentGlobal: ElementRef;

	currentMainImageIndex = 0;
	itemInTrolley = 0;
	listErrored = false;
	listErrorMessage: ListErrorMessages;
	product: ProductDetails;
	failedProductFetch = false;
	savedLists$: BehaviorSubject<ShoppingListItem[]> = new BehaviorSubject<ShoppingListItem[]>([]);
	mhlsPickupAddress?: FulfilmentStorePickupAddressResponse;
	mhlsStoreName?: string;
	mhlsPickupAddressDetails?: string;
	stockLevel = StockLevelEnum.NoStock;

	trackingEvent = TrackingEvent;
	notificationType = NotificationType;

	// Feature flags
	isNewPDPEnabled$: Observable<boolean>;
	isNewPDPTabEnabled$: Observable<boolean>;
	tabindex$: Observable<number>;
	newPDPTabEnabled = false;

	shouldShowDetailTop: boolean;

	public countryOfOrigin$: Observable<string | null>;
	private mhlsFulfilmentStoreId?: string;
	private _showNotification = false;
	private destroyed$: Subject<boolean> = new Subject();

	constructor(
		private breadCrumbService: BreadCrumbService,
		private cdr: ChangeDetectorRef,
		private datalayerService: DatalayerService,
		private dynamicContentService: DynamicContentService,
		private featureService: FeatureService,
		private fulfilmentStoreService: FulfilmentStoreService,
		public productService: ProductService,
		private route: ActivatedRoute,
		private metaDataService: MetaDataService,
		private appSettingsService: AppSettingsService,
		private titleCasePipe: TitleCasePipe,
		private tealiumService: TealiumUtagService,
		private modalRoutingService: ModalRoutingService,
		private pricingService: PricingService
	) {}

	get unit(): ProductResponse.UnitEnum | undefined {
		if (this.productService.supportsBothEachAndKgPricing) {
			return this.productService.selectedPurchasingUnit;
		}

		return this.productService.unit;
	}

	get boostOfferTag(): ProductTag | undefined {
		const tags: ProductTag[] = this.product.productTags || [];
		return tags.find((tag: ProductTag) => tag.tagType === 'IsBoostOffer');
	}

	get productTags(): ProductTag[] {
		const tags = [...(this.product.productTags || [])];

		const boostOfferTag = this.boostOfferTag;
		if (boostOfferTag) {
			tags.splice(tags.indexOf(boostOfferTag), 1);
		}

		return tags;
	}

	get isEachKgItemAdded(): boolean | undefined {
		return this.productService.isEachKgItemAdded;
	}

	get isItemAlreadyAddedByKg(): boolean {
		return (
			!!this.productService.isEachKgItemAdded &&
			this.productService.selectedPurchasingUnit === ProductResponse.UnitEnum.Kg
		);
	}

	get productDepartmentName(): string | undefined {
		return this.product.breadcrumb?.department?.name;
	}

	get isEachKgMeatProduct(): boolean {
		return !!(this.productService.supportsBothEachAndKgPricing && this.productDepartmentName?.match(/meat/i));
	}

	get productTitle(): string | undefined {
		return this.isEachKgMeatProduct && this.product?.size?.volumeSize
			? `${this.product?.name} ${this.product?.size?.volumeSize}`
			: this.product?.name;
	}

	get isProductTagClubPrice(): boolean {
		const tags = this.product.productTags || [];
		const primaryTag = tags[0]?.tagType;
		return primaryTag === tagTypeEnum.IsClubPrice;
	}

	get cupPrice(): string | false {
		return this.productService.cupPrice;
	}

	get ingredients(): string {
		return this.product?.ingredients?.ingredients?.join(', ') || '';
	}

	get ingredientFootnotes(): string[] {
		return this.product?.ingredients?.footnotes || [];
	}

	get nutrition(): NutritionResponse {
		return (this.product?.nutrition || [])[0];
	}

	get allergens(): string {
		return this.product?.allergens?.join(', ') || '';
	}

	get allergenMaybePresent(): string | undefined {
		return this.product?.allergenMaybePresent;
	}

	get directions(): string | undefined {
		return this.product?.directions;
	}

	get servingSuggestion(): string | undefined {
		return this.product?.servingSuggestion;
	}

	get claims(): string {
		return this.product?.claims?.join(', ') || '';
	}

	get endorsements(): string {
		return this.product?.endorsements?.join(', ') || '';
	}

	get warnings(): string {
		return this.product?.warnings?.join(', ') || '';
	}

	get contents(): string {
		return this.product?.contents?.join(', ') || '';
	}

	get category(): string | undefined {
		return this.product?.category; // ProductResponse
	}

	get inTrolleyText(): string {
		return `${this.itemInTrolley}${this.isKgItem ? 'kg' : ''} in trolley`;
	}

	get isKgItem(): boolean {
		return this.unit === ProductResponse.UnitEnum.Kg;
	}

	get price(): number | undefined {
		return this.productService.salePrice;
	}

	get productIsAvailable(): boolean {
		return this.productService.productIsAvailable;
	}

	get productIsInStock(): boolean {
		return this.productService.productIsInStock;
	}

	get quantityResponse(): QuantityResponse | undefined {
		return this.productService.quantityResponse;
	}

	get mainImage(): ImageInfo {
		return (this.product?.images || [])[this.currentMainImageIndex];
	}

	get showNotification(): boolean {
		return this._showNotification;
	}

	get isMerchantStorefrontEnabled(): boolean {
		return !!this.mhlsFulfilmentStoreId && !!this.mhlsPickupAddressName;
	}

	get mhlsPickupAddressName(): string {
		return this.mhlsPickupAddress?.name || '';
	}

	get topUniqueId(): string {
		return this.generateUniqueId('top');
	}

	get bottomUniqueId(): string {
		return this.generateUniqueId('bottom');
	}

	set showNotification(show: boolean) {
		if (!this._showNotification && show) {
			this._showNotification = show;

			setTimeout(
				() => {
					this._showNotification = false;
					this.cdr.markForCheck();
				},
				parseInt(this.appSettingsService.getTransition('longer'), 10)
			);
		}
	}

	checkStockInOtherStores(): void {
		this.modalRoutingService.openWithOptions(
			MODAL_ROUTES.PRODUCT_DETAILS_MODAL,
			{
				state: { product: this.product, productService: this.productService },
				skipLocationChange: true,
			},
			MODAL_ROUTES.PRODUCT_STORES_STOCK_LEVEL_MODAL_PATH
		);
	}

	showMHLSModal(): void {
		const mhlsPickupAddressId = this.mhlsPickupAddress?.addressId;

		if (
			this.isMerchantStorefrontEnabled &&
			this.productService.canOpenChangePickupStoreModal(mhlsPickupAddressId) &&
			this.productService.shopper.isShopper
		) {
			return this.productService.openChangePickupStoreModal(mhlsPickupAddressId, this.mhlsPickupAddressName);
		}
	}

	roundelText({ points, value, type }: Omit<RewardValue, 'display'>): EDRRoundelText {
		return mapRewardTypeToRoundelText({ points, value, type });
	}

	addToTrolleyClick(): void {
		this.productService.addMinimumProductQuantity();
	}

	fetchSavedLists(): void {
		this.listErrored = false;
		if (!this.product?.sku) {
			return;
		}
		this.productService
			.getSavedListsWithProduct(this.product?.sku)
			.pipe(
				take(1),
				catchError((e) => this.handleListFailure(e, ListErrorMessages.LoadFailed))
			)
			.subscribe((r) => this.savedLists$.next(r));
	}

	handleListAccordionHeadingClick(isOpened: boolean): void {
		if (isOpened) {
			return this.fetchSavedLists();
		}

		this.listErrored = false;
	}

	handleListFailure = (e: string, message: ListErrorMessages): Observable<string> => {
		this.listErrorMessage = message;
		this.listErrored = true;

		this.cdr.markForCheck();

		return of(e);
	};

	handlePurchasingUnitChange(purchasingUnit: ProductResponse.UnitEnum): void {
		this.productService.setPurchasingUnit(purchasingUnit);
	}

	handleProductStateChange = (productState: ProductState): void => {
		if (!productState) {
			return;
		}
		this.product = productState.product as ProductDetailsResponse;
		// TODO: Tidy up when refactoring breadcrumb api logic
		const breadcrumb = (<ProductDetailsResponse>productState.product || {}).breadcrumb;
		if (breadcrumb) {
			this.breadCrumbService.setBreadcrumbs('ProductDetails', breadcrumb, undefined, {
				text: (productState.product || {}).name || '',
				routeUrl: undefined,
				queryParams: {},
			});
		}

		if (
			this.itemInTrolley &&
			productState.completed &&
			parseFloat(productState.quantityInTrolley.toString()) &&
			this.product?.quantity?.value !== productState.quantityInTrolley
		) {
			this.showNotification = true;
		}

		if (productState.completed && !productState.submitted) {
			this.itemInTrolley = productState.quantityInTrolley;
		}

		this.cdr.markForCheck();
	};

	ngOnInit(): void {
		this.route.data.pipe(takeUntil(this.destroyed$)).subscribe((data) => {
			this.product = data.product;
			this.mhlsFulfilmentStoreId =
				this.route.snapshot.queryParams?.ostore || this.route.snapshot.queryParams?.store;
			this.failedProductFetch = !!data.product?.apiFailed;

			if (!this.product || this.failedProductFetch) {
				return;
			}

			this.initDetailPage();
			this.initDynamicContent();
			this.linkProductDetailTealium();
			this.showMHLSModal();
		});

		// TODO SSR why does this fire twice?
		this.productService.state$.pipe(takeUntil(this.destroyed$)).subscribe(this.handleProductStateChange);

		this.fulfilmentStoreService.state$
			.pipe(takeUntil(this.destroyed$), distinctUntilFulfilmentChanged(), skip(1))
			.subscribe(() => {
				this.productService.refetchProductDetail();
				this.initDynamicContent();
			});

		this.isNewPDPEnabled$ = this.featureService.isEnabled(ContextResponse.EnabledFeaturesEnum.EnablePDPNewDesign);
		this.shouldShowDetailTop = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.EnablePDPNewDesign
		);

		this.isNewPDPTabEnabled$ = this.featureService.isEnabled(ContextResponse.EnabledFeaturesEnum.EnablePDPTabOrder);

		// This observable sets the tabindex value based on the isNewPDPEnabled$ feature toggle.
		this.tabindex$ = this.isNewPDPTabEnabled$.pipe(map((isEnabled) => (isEnabled ? 0 : -1)));

		// Subscribe to isNewPDPTabEnabled$ and store its value
		this.isNewPDPTabEnabled$.pipe(takeUntil(this.destroyed$)).subscribe((isEnabled) => {
			this.newPDPTabEnabled = isEnabled;
		});
	}

	initDynamicContent(): void {
		if (!this.product) {
			return;
		}

		this.dynamicContentService.setViewChildForOutlet(
			this.shelfBanner,
			DynamicContentTargets.dynamicContentShelfBanner
		);
		this.dynamicContentService.setViewChildForOutlet(
			this.headerBanner,
			DynamicContentTargets.dynamicContentHeadertBanner
		);
		this.dynamicContentService.setViewChildForOutlet(
			this.dynamicContentGlobal,
			DynamicContentTargets.dynamicContentGlobalPanel
		);
		this.dynamicContentService.init(<BannerTarget>{
			skus: [this.product.sku],
			targetContext: DynamicContentRequest.TargetContextEnum.ProductDetail,
		});
	}

	ngOnDestroy(): void {
		// unset the viewchild ref for DC
		this.destroyed$.next(true);
		this.destroyed$.complete();
		this.dynamicContentService.clearViewChildForOutlet(DynamicContentTargets.dynamicContentShelfBanner);
		this.dynamicContentService.clearViewChildForOutlet(DynamicContentTargets.dynamicContentHeadertBanner);
		this.dynamicContentService.clearViewChildForOutlet(DynamicContentTargets.dynamicContentGlobalPanel);
	}

	@HostListener('keydown', ['$event'])
	handleKeydown(event: KeyboardEvent): void {
		if (!this.newPDPTabEnabled) {
			return;
		}
		if (event.key === 'Enter') {
			const target = event.target as HTMLElement;
			const index = target.getAttribute('data-index');
			if (index !== null) {
				this.setAsMainImage(Number(index));
			}
		}
	}

	setAsMainImage(index: number): void {
		this.currentMainImageIndex = index;
	}

	trackByFn: TrackByFunction<AccordionItemComponent> = (_, item) => item.heading;

	toggleProductInList(id: string, inList: boolean): void {
		if (!inList) {
			this.productService
				.addToList(id, [{ sku: this.product.sku, quantity: this.productService.quantityInTrolley }])
				.pipe(
					take(1),
					catchError((e) => this.handleListFailure(e, ListErrorMessages.SaveFailed))
				)
				.subscribe(() => {
					this.fetchSavedLists();
				});
			return;
		}

		this.productService
			.removeFromList(id)
			.pipe(take(1), catchError(catchError((e) => this.handleListFailure(e, ListErrorMessages.RemoveFailed))))
			.subscribe(() => {
				this.fetchSavedLists();
			});
	}

	updateQuantity(quantity: number): void {
		this.productService.addOrUpdateTrolley(quantity);
	}

	private generateUniqueId(position: string): string {
		return this.product.sku + '-' + position;
	}

	private linkProductDetailTealium(): void {
		const tealiumProductList: TealiumProductList = {
			product_category_1: [`${this.product.breadcrumb?.department?.name || ''}`],
			product_category_2: [`${this.product.breadcrumb?.aisle?.name || ''}`],
			product_category_3: [`${this.product.breadcrumb?.shelf?.name || ''}`],
			product_sku: [this.product.sku || ''],
			product_image_url: [this.mainImage?.big || ''],
			product_name: [this.product.name || ''],
			product_price: [this.product.price?.originalPrice?.toString() || defaultSalesPrice],
			product_sale_price: [this.product.price?.salePrice?.toString() || defaultSalesPrice],
			product_stock: [isProductAvailableString(this.productIsAvailable)],
			product_brand: [''],
			product_promo_category: [''],
			product_promo_value: [''],
			product_dietary: [''],
			product_quantity: [this.quantityResponse?.value?.toString() || ''],
			search_value: [],
			search_results_returned: [],
		};
		this.tealiumService.link({
			...productDetailsConstants,
			...tealiumProductList,
		});
	}

	private initDetailPage(): void {
		const productName = this.product.name || '';
		const productType = getProductType(this.isContentInGridCarousel(this.product), this.product.adId);
		const discountValue = getDiscountDiffValue(this.product?.price?.originalPrice, this.product?.price?.salePrice);

		this.metaDataService.setPageMeta(
			this.appSettingsService.getPageTitle('productDetail', this.titleCasePipe.transform(productName)),
			this.appSettingsService.getMetaTitle('productDetail', productName),
			this.appSettingsService.getMetaDescription('productDetail', productName),
			this.appSettingsService.getMetaImage('default'),
			this.appSettingsService.getMetaUrl('default'),
			{ productName: kebabCase(productName) }
		);

		if (this.product?.sku) {
			// GA3 productDetail event
			this.datalayerService.trackProductDetailView(
				this.product?.sku,
				this.product.productTags?.[0]?.tagType,
				this.product.price,
				this.product.adId
			);

			// GA4 view_item event
			const ecommerce = {
				currency: pageAnalytics.currency,
				value: this.product.price?.salePrice,
				items: [
					{
						item_id: this.product.sku,
						item_name: this.product.name,
						discount: discountValue,
						item_brand: this.product.brand,
						item_category: this.product.breadcrumb?.department?.name,
						item_category2: this.product.breadcrumb?.aisle?.name,
						item_category3: this.product.breadcrumb?.shelf?.name,
						item_variant: productType,
						price: this.product.price?.salePrice,
						availability_status: this.product.availabilityStatus,
						promotionProductTags: this.product.productTags?.map(({ tagType }) => tagType),
					},
				],
			};
			this.tealiumService.view({
				...productViewConstants,
				ecommerce,
			});
		}

		this.productService.setState({
			product: this.product,
			quantityInTrolley: this.product.quantity?.value,
		});

		this.setStockLevels();
		this.handleFeatureFlags();
	}

	private isContentInGridCarousel(response: CallToActionResponse | ProductResponse): boolean {
		return (response as ProductResponse).type === 'PromotionalCarousel';
	}

	private handleFeatureFlags(): void {
		this.countryOfOrigin$ = this.featureService.state$.pipe(
			mergeMap((features) => {
				if (features.enabledFeatures.includes(ContextResponse.EnabledFeaturesEnum.CountryOfOriginFromPAS)) {
					return this.getCountryOfOriginFromService();
				}

				return of(this.product?.origins?.[0] ?? null);
			})
		);
	}

	private getCountryOfOriginFromService = (): Observable<string | null> =>
		this.pricingService.getCountryOfOrigin(this.product.sku ?? '', this.product.storeInfo?.site?.id).pipe(
			map((pasOrigin) => {
				if (pasOrigin && pasOrigin.countryName) {
					return pasOrigin.countryName; // CRPIP-2711 will replace this with coo statement once ready.
				}

				return this.product?.origins?.[0] ?? null;
			})
		);

	private setStockLevels(): void {
		const productStoresStockLevel: ProductStoresStockLevelResponse | undefined =
			this.product.productStoresStockLevel;

		if (productStoresStockLevel?.fulfilmentStoreStockLevels) {
			this.mhlsPickupAddress = productStoresStockLevel.fulfilmentStoreStockLevels[0].pickupAddress;
			this.stockLevel =
				productStoresStockLevel.fulfilmentStoreStockLevels[0].stockLevel || StockLevelEnum.NoStock;
			this.mhlsPickupAddressDetails = undefined;
			this.mhlsStoreName = this.mhlsPickupAddress?.name || this.product.storeInfo?.site?.name || undefined;

			if (this.product.storeInfo && this.product.storeInfo.site) {
				this.mhlsPickupAddressDetails = `${this.product.storeInfo.site.addressLine1}, ${this.product.storeInfo.site.suburb}`;
			}

			// We want to show the customer that there is no stock if the product is not ranged at a specific fulfilment store
			if (this.stockLevel === StockLevelEnum.Unknown) {
				this.stockLevel = StockLevelEnum.NoStock;
			}
		}
	}
}
